import { z } from "zod";
import type { ProjectState } from "./replicants.js";

const sideSchema = z.enum(["home", "away"]);

/** What EFIRGA executes for a cue (the Efir view runs it through the same
 *  hooks as the other views). */
export const previewActionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("show"), mode: z.string().min(1) }),
  z.object({ kind: z.literal("hide"), mode: z.string().min(1) }),
  z.object({
    kind: z.literal("event"),
    type: z.enum(["goal", "penalty_goal", "own_goal", "yellow_card", "second_yellow", "red_card"]),
    side: sideSchema,
    playerId: z.string().min(1),
  }),
  z.object({
    kind: z.literal("subs"),
    side: sideSchema,
    pairs: z.array(z.object({ outId: z.string().min(1), inId: z.string().min(1) })).min(1).max(5),
  }),
]);
export type PreviewAction = z.infer<typeof previewActionSchema>;

/** A cue: rendered by the overlay page in `?preview`, taken to air on EFIRGA. */
export const previewSchema = z.object({
  mode: z.string().nullable(),
  data: z.unknown().optional(),
  scoreDelta: z.object({ home: z.number().int(), away: z.number().int() }).optional(),
  label: z.string(),
  action: previewActionSchema,
  setAt: z.number().int().nonnegative(),
});
export type Preview = z.infer<typeof previewSchema>;

/** The state the PREVIEW graphic renders: the cued overlay and score. With no
 *  cue it is the on-air state itself, so PREVIEW mirrors PROGRAM. */
export function previewState(state: ProjectState): ProjectState {
  const pv = state.preview;
  if (!pv) return state;
  const delta = pv.scoreDelta;
  return {
    ...state,
    overlay: { mode: pv.mode, data: pv.data, shownAt: pv.setAt },
    match: delta
      ? {
          ...state.match,
          homeScore: Math.max(0, state.match.homeScore + delta.home),
          awayScore: Math.max(0, state.match.awayScore + delta.away),
        }
      : state.match,
  };
}
